import { useGraphQLQuery } from '@betterment/js-runtime';
import { parse } from 'date-fns';
import invariant from 'tiny-invariant';

import { ErrorMap } from '~/b4b/components/graphql-mutation-form/GraphQLMutationForm';
import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { BenefitReportInputOptionType } from '~/graphql/schema';

import { BenefitReportCreationOption } from './data';
import { CreateBenefitReportMutationVariables } from './graphql/mutation.createReport.generated';
import { ReportCreationOptionsDocument, ReportCreationOptionsQuery } from './graphql/query.options.generated';

function snakeToCamelCase(snakeCaseString: string): string {
  if (!snakeCaseString.includes('_')) {
    return snakeCaseString;
  }

  return snakeCaseString.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());
}

function findSelectedReport({
  values,
  options,
}: {
  values: BenefitReportCreationOption;
  options: ReportCreationOptionsQuery['employer']['reportCreationOptions'];
}): undefined | ReportCreationOptionsQuery['employer']['reportCreationOptions'][0] {
  const { type } = values;
  return type ? options?.find(report => report.type === type) : undefined;
}

// Queries graphql for the list of available report options. Camel cases attribute names
// for better compatibility with our mutation
export function useReportOptions() {
  const employerId = useCurrentEmployerId();

  const { data } = useGraphQLQuery(ReportCreationOptionsDocument, {
    variables: { employerId },
    throwOnError: true,
  });

  const withTransformedAttributeNames = (data?.employer.reportCreationOptions || []).map(report => ({
    ...report,
    inputs: report.inputs.map(input => ({ ...input, attribute: snakeToCamelCase(input.attribute) })),
  }));

  return {
    reportableYears: data ? data.employer.reportableYears : [],
    options: data ? withTransformedAttributeNames : undefined,
    isLoading: !data,
  };
}

// Custom hook to invoke the report creation mutation. Ensures we properly handle or throw on
// any server errors, or when we get unexpected validation errors back. Also handles advancing the flow
// forward to the next step
export function useCreateBenefitFormHelpers() {
  const employerId = useCurrentEmployerId();
  const { options } = useReportOptions();

  const prepareValues: (values: BenefitReportCreationOption) => CreateBenefitReportMutationVariables = values => {
    const { type } = values;
    invariant(options, 'No report options availalbe');
    const selectedReport = findSelectedReport({ values, options });
    invariant(selectedReport && type, 'No selected report in options');

    const relevantAttributes = selectedReport.inputs.map(input => input.attribute);
    const inputTypes = Object.fromEntries(selectedReport.inputs.map(input => [input.attribute, input.type]));

    // In case some extra values were leftover by formik, we want to  strip those out
    // and only submit values directly relevant to this report
    const relevantValues = Object.fromEntries(
      Object.entries(values)
        .filter(([key]) => relevantAttributes.includes(key))
        .map(([key, value]) => {
          const inputType = inputTypes[key];
          // Formik date fields represent dates as MM/dd/yyyy, but
          // when we submit them to the server, we need ISO.
          if (inputType === BenefitReportInputOptionType.Date && typeof value === 'string') {
            return [key, parse(value, 'MM/dd/yyyy', new Date()).toISOString()];
          }
          return [key, value];
        })
    );

    return {
      employerId,
      type,
      reportInput: relevantValues,
    };
  };

  function buildErrorHandlers(values: BenefitReportCreationOption): ErrorMap<BenefitReportCreationOption> {
    invariant(options, 'No report options availalbe');
    const selectedReport = findSelectedReport({ values, options });

    invariant(selectedReport, 'No selected report');

    return Object.fromEntries(
      selectedReport.inputs.map(input => {
        const path = ['reportInput', input.attribute].join('/');
        return [path, input.attribute];
      })
    );
  }

  return {
    prepareValues,
    buildErrorHandlers,
  };
}
